import { Suspense, lazy, ElementType } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => (
  <Suspense fallback={false}>
    <Component {...props} />
  </Suspense>
);

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        { element: <Navigate to={'login'} replace />, index: true },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
      ],
    },

    // Dashboard Routes (Phase 1 Core Admin)
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/dashboard/overview" replace />, index: true },

        // 1. Dashboard Overview
        { path: 'overview', element: <Overview /> },

        // 2. Clients
        { path: 'clients', element: <Navigate to="/dashboard/clients/all" replace /> },
        { path: 'clients/all', element: <ClientsList /> },
        { path: 'clients/active', element: <ClientsList /> },
        { path: 'clients/trial', element: <ClientsList /> },
        { path: 'clients/expired', element: <ClientsList /> },
        { path: 'clients/blocked', element: <ClientsList /> },
        { path: 'clients/deleted', element: <ClientsList /> },
        { path: 'clients/:id', element: <ClientDetails /> },

        // 3. Field Agents
        { path: 'agents', element: <Navigate to="/dashboard/agents/all" replace /> },
        { path: 'agents/all', element: <AgentsList /> },
        { path: 'agents/active', element: <AgentsList /> },
        { path: 'agents/inactive', element: <AgentsList /> },
        { path: 'agents/deleted', element: <AgentsList /> },

        // 4. Plans & Subscriptions
        { path: 'plans', element: <PlansList /> },
        { path: 'subscriptions', element: <SubscriptionsList /> },
        { path: 'trial-settings', element: <TrialSettingsPage /> },

        // 5. Promo Codes
        { path: 'promo-codes', element: <Navigate to="/dashboard/promo-codes/all" replace /> },
        { path: 'promo-codes/all', element: <PromoCodesList /> },
        { path: 'promo-codes/expired', element: <PromoCodesList /> },
        { path: 'promo-codes/partner-code-settings', element: <PartnerCodeSettingsPage /> },

        // 6. Payments & Invoices
        { path: 'payments', element: <Navigate to="/dashboard/payments/all" replace /> },
        { path: 'payments/all', element: <PaymentsList /> },
        { path: 'payments/successful', element: <PaymentsList /> },
        { path: 'payments/pending', element: <PaymentsList /> },
        { path: 'payments/failed', element: <PaymentsList /> },
        { path: 'invoices', element: <InvoicesList /> },

        // 7. Website Leads
        { path: 'leads', element: <Navigate to="/dashboard/leads/queries" replace /> },
        { path: 'leads/queries', element: <QueriesList /> },
        { path: 'leads/partner-requests', element: <PartnerRequestsList /> },

        // 8. Voter Signs
        { path: 'voter-signs', element: <VoterSignsList /> },
        { path: 'polling-schedules', element: <PollingSchedulesList /> },
        { path: 'locations', element: <LocationMasterPage /> },

        // 10. Partners (Become a Partner module, owner side)
        { path: 'partners', element: <Navigate to="/dashboard/partners/all" replace /> },
        { path: 'partners/all', element: <PartnersList /> },
        { path: 'partners/pending', element: <PartnersList /> },
        { path: 'partners/approved', element: <PartnersList /> },
        { path: 'partners/rejected', element: <PartnersList /> },
        { path: 'partners/suspended', element: <PartnersList /> },
        { path: 'partners/deleted', element: <PartnersList /> },
        { path: 'partners/:id', element: <PartnerDetails /> },
        { path: 'partner-orders', element: <PartnerOrdersList /> },
        { path: 'partners/location-requests', element: <PartnerLocationRequestsList /> },

        // 11. Commissions (Partner Phase Two, owner side)
        { path: 'commissions', element: <Navigate to="/dashboard/commissions/rules" replace /> },
        { path: 'commissions/settings', element: <CommissionSettingsPage /> },
        { path: 'commissions/rules', element: <CommissionRulesList /> },
        { path: 'commissions/ledger', element: <CommissionLedgerList /> },
        { path: 'commissions/payouts', element: <PayoutsPage /> },
        { path: 'commissions/unconfigured', element: <UnconfiguredOrdersList /> },
        { path: 'commissions/reports', element: <PartnerReportsPage /> },

        // 12. KYC (Partner Phase Four, owner side)
        { path: 'kyc', element: <Navigate to="/dashboard/kyc/queue" replace /> },
        { path: 'kyc/settings', element: <KycSettingsPage /> },
        { path: 'kyc/queue', element: <KycQueueList /> },
        { path: 'kyc/review/:id', element: <PartnerKycReview /> },

        // 13. Material Wallets (Partner Phase 11a, owner side)
        { path: 'wallets', element: <Navigate to="/dashboard/wallets/overview" replace /> },
        { path: 'wallets/overview', element: <WalletSummaryPage /> },
        { path: 'wallets/all', element: <WalletsListPage /> },
        { path: 'wallets/recharges', element: <WalletRechargesListPage /> },
        { path: 'wallets/reconcile', element: <WalletReconciliationPage /> },
        { path: 'wallets/settings', element: <WalletSettingsPage /> },

        // 14. Material Orders & Catalog (Partner Phase 11b, 11c & 11d, owner side)
        { path: 'material', element: <Navigate to="/dashboard/material/orders" replace /> },
        { path: 'material/orders', element: <MaterialOrdersListPage /> },
        { path: 'material/reports', element: <MaterialReportsPage /> },
        { path: 'material/products', element: <MaterialProductsPage /> },
        { path: 'material/settings', element: <MaterialSettingsPage /> },
        { path: 'material/stuck', element: <MaterialStuckJobsPage /> },
        { path: 'material/postcards', element: <MaterialPostcardsPage /> },
        { path: 'material/slip-data', element: <MaterialSlipDataPage /> },

        // 15. Upload Requests & Updates (mobile app users)
        { path: 'upload-requests', element: <UploadRequestsList /> },
        { path: 'upload-requests/:id', element: <UploadRequestDetail /> },
        { path: 'upload-pdf', element: <RollLibraryPage /> },
        { path: 'announcements', element: <AnnouncementsList /> },
        { path: 'announcements/new', element: <NewAnnouncement /> },

        // The bell's "view all" (every login sees the events of its roles)
        { path: 'notifications', element: <NotificationsPage /> },

        // 16. Deleted accounts log (migration 0035)
        { path: 'deleted-accounts', element: <DeletedAccountsList /> },

        // 17. Staff users, activity log and login history (migration 0037, owner only)
        { path: 'staff', element: <StaffList /> },
        { path: 'staff/:id', element: <StaffDetails /> },
        { path: 'activity-log', element: <ActivityLogList /> },
        { path: 'login-history', element: <LoginHistoryList /> },

        // 9. Settings
        { path: 'settings', element: <SettingsPage /> },
        { path: 'settings/social-platforms', element: <SocialPlatformsPage /> },
      ],
    },

    // Main / Fallback Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      children: [
        { element: <Navigate to={'/dashboard/overview'} replace />, index: true },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));

// CORE ADMIN PAGES
const Overview = Loadable(lazy(() => import('../pages/dashboard/Overview')));
const ClientsList = Loadable(lazy(() => import('../pages/clients/ClientsList')));
const ClientDetails = Loadable(lazy(() => import('../pages/clients/ClientDetails')));
const AgentsList = Loadable(lazy(() => import('../pages/agents/AgentsList')));
const PlansList = Loadable(lazy(() => import('../pages/plans/PlansList')));
const SubscriptionsList = Loadable(lazy(() => import('../pages/subscriptions/SubscriptionsList')));
const TrialSettingsPage = Loadable(lazy(() => import('../pages/subscriptions/TrialSettingsPage')));
const PromoCodesList = Loadable(lazy(() => import('../pages/promoCodes/PromoCodesList')));
const MaterialSlipDataPage = Loadable(lazy(() => import('../pages/material/MaterialSlipDataPage')));
const UploadRequestsList = Loadable(lazy(() => import('../pages/uploadRequests/UploadRequestsList')));
const UploadRequestDetail = Loadable(lazy(() => import('../pages/uploadRequests/UploadRequestDetail')));
const RollLibraryPage = Loadable(lazy(() => import('../pages/rollLibrary/RollLibraryPage')));
const AnnouncementsList = Loadable(lazy(() => import('../pages/announcements/AnnouncementsList')));
const NewAnnouncement = Loadable(lazy(() => import('../pages/announcements/NewAnnouncement')));
const NotificationsPage = Loadable(lazy(() => import('../pages/notifications/NotificationsPage')));
const PartnerCodeSettingsPage = Loadable(lazy(() => import('../pages/promoCodes/PartnerCodeSettingsPage')));
const PaymentsList = Loadable(lazy(() => import('../pages/payments/PaymentsList')));
const InvoicesList = Loadable(lazy(() => import('../pages/payments/InvoicesList')));
const QueriesList = Loadable(lazy(() => import('../pages/websiteLeads/QueriesList')));
const PartnerRequestsList = Loadable(lazy(() => import('../pages/websiteLeads/PartnerRequestsList')));
const VoterSignsList = Loadable(lazy(() => import('../pages/voterSigns/VoterSignsList')));
const PollingSchedulesList = Loadable(lazy(() => import('../pages/pollingSchedules/PollingSchedulesList')));
const LocationMasterPage = Loadable(lazy(() => import('../pages/locations/LocationMasterPage')));
const PartnersList = Loadable(lazy(() => import('../pages/partners/PartnersList')));
const PartnerDetails = Loadable(lazy(() => import('../pages/partners/PartnerDetails')));
const PartnerOrdersList = Loadable(lazy(() => import('../pages/partners/PartnerOrdersList')));
const PartnerLocationRequestsList = Loadable(
  lazy(() => import('../pages/partners/PartnerLocationRequestsList'))
);
const CommissionSettingsPage = Loadable(lazy(() => import('../pages/commissions/CommissionSettingsPage')));
const CommissionRulesList = Loadable(lazy(() => import('../pages/commissions/CommissionRulesList')));
const CommissionLedgerList = Loadable(lazy(() => import('../pages/commissions/CommissionLedgerList')));
const PayoutsPage = Loadable(lazy(() => import('../pages/commissions/PayoutsPage')));
const KycSettingsPage = Loadable(lazy(() => import('../pages/kyc/KycSettingsPage')));
const KycQueueList = Loadable(lazy(() => import('../pages/kyc/KycQueueList')));
const PartnerKycReview = Loadable(lazy(() => import('../pages/kyc/PartnerKycReview')));
const UnconfiguredOrdersList = Loadable(lazy(() => import('../pages/commissions/UnconfiguredOrdersList')));
const PartnerReportsPage = Loadable(lazy(() => import('../pages/commissions/PartnerReportsPage')));
const DeletedAccountsList = Loadable(lazy(() => import('../pages/deletedAccounts/DeletedAccountsList')));
const StaffList = Loadable(lazy(() => import('../pages/staff/StaffList')));
const StaffDetails = Loadable(lazy(() => import('../pages/staff/StaffDetails')));
const ActivityLogList = Loadable(lazy(() => import('../pages/audit/ActivityLogList')));
const LoginHistoryList = Loadable(lazy(() => import('../pages/audit/LoginHistoryList')));
const SettingsPage = Loadable(lazy(() => import('../pages/settings/SettingsPage')));
const SocialPlatformsPage = Loadable(lazy(() => import('../pages/settings/SocialPlatformsPage')));

// MATERIAL WALLETS (Phase 11a)
const WalletSummaryPage = Loadable(lazy(() => import('../pages/wallets/WalletSummaryPage')));
const WalletsListPage = Loadable(lazy(() => import('../pages/wallets/WalletsListPage')));
const WalletRechargesListPage = Loadable(lazy(() => import('../pages/wallets/WalletRechargesListPage')));
const WalletReconciliationPage = Loadable(lazy(() => import('../pages/wallets/WalletReconciliationPage')));
const WalletSettingsPage = Loadable(lazy(() => import('../pages/wallets/WalletSettingsPage')));

// MATERIAL ORDERS (Phase 11b, 11c & 11d)
const MaterialOrdersListPage = Loadable(lazy(() => import('../pages/material/MaterialOrdersListPage')));
const MaterialReportsPage = Loadable(lazy(() => import('../pages/material/MaterialReportsPage')));
const MaterialProductsPage = Loadable(lazy(() => import('../pages/material/MaterialProductsPage')));
const MaterialSettingsPage = Loadable(lazy(() => import('../pages/material/MaterialSettingsPage')));
const MaterialStuckJobsPage = Loadable(lazy(() => import('../pages/material/MaterialStuckJobsPage')));
const MaterialPostcardsPage = Loadable(lazy(() => import('../pages/material/MaterialPostcardsPage')));

// MAIN ERROR PAGES
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
